/**
 * Theme Provider Component
 *
 * Manages theme state (light/dark/system) and applies the active
 * shadcn/ui theme preset's CSS variables to document.documentElement.
 *
 * Integrates with both the legacy --theme-* variable system AND
 * the new shadcn --variable convention (used by Tailwind colors).
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ACTIVE_THEME, themePresets } from '../../../../config/ui/theme/theme-presets';
import { themeConfig, getThemeVariables, getContrastText } from '../../../../config/ui/theme/theme-config';
import '../../../../config/ui/theme/theme-styles.css';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  mode: ThemeMode;
  actualMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  getContrastText: (backgroundColor: string) => string;
  colors: typeof themeConfig.colors;
  spacing: typeof themeConfig.spacing;
  typography: typeof themeConfig.typography;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultMode = 'system',
  storageKey = 'theme-mode'
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(defaultMode);
  const [actualMode, setActualMode] = useState<'light' | 'dark'>('light');

  const getSystemPreference = useCallback((): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, []);

  const calculateActualMode = useCallback((themeMode: ThemeMode): 'light' | 'dark' => {
    if (themeMode === 'system') {
      return getSystemPreference();
    }
    return themeMode;
  }, [getSystemPreference]);

  // Initialize from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      setModeState(stored as ThemeMode);
    }
  }, [storageKey]);

  // Update actual mode when mode changes
  useEffect(() => {
    const actual = calculateActualMode(mode);
    setActualMode(actual);
  }, [mode, calculateActualMode]);

  // Apply theme to document — both shadcn preset and legacy system
  useEffect(() => {
    const root = document.documentElement;

    // Prevent flash during theme switch
    root.classList.add('theme-transitioning');

    // 1) Apply shadcn CSS variables from active preset
    const preset = themePresets[ACTIVE_THEME][actualMode];
    for (const [key, value] of Object.entries(preset)) {
      root.style.setProperty(`--${key}`, value);
    }

    // 2) Toggle dark class for Tailwind darkMode: ['class']
    if (actualMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // 3) Legacy theme system: data-theme attribute + body classes
    root.setAttribute('data-theme', actualMode);
    const variables = getThemeVariables(actualMode);
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    if (actualMode === 'dark') {
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    }

    const timeout = setTimeout(() => {
      root.classList.remove('theme-transitioning');
    }, 50);

    return () => clearTimeout(timeout);
  }, [actualMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setActualMode(e.matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [mode]);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(storageKey, newMode);
  }, [storageKey]);

  const toggleMode = useCallback(() => {
    const newMode = actualMode === 'light' ? 'dark' : 'light';
    setMode(newMode);
  }, [actualMode, setMode]);

  const value: ThemeContextValue = {
    mode,
    actualMode,
    setMode,
    toggleMode,
    getContrastText,
    colors: themeConfig.colors,
    spacing: themeConfig.spacing,
    typography: themeConfig.typography,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to use theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Theme toggle button component
 */
export function ThemeToggle({ className = '' }: { className?: string }) {
  const { mode, actualMode, setMode } = useTheme();

  const handleClick = () => {
    const nextMode: ThemeMode =
      mode === 'light' ? 'dark' :
      mode === 'dark' ? 'system' :
      'light';
    setMode(nextMode);
  };

  const getIcon = () => {
    if (mode === 'system') {
      return actualMode === 'dark' ? '🌙' : '☀️';
    }
    return mode === 'dark' ? '🌙' : '☀️';
  };

  const getLabel = () => {
    if (mode === 'system') {
      return `System (${actualMode})`;
    }
    return mode === 'dark' ? 'Dark' : 'Light';
  };

  return (
    <button
      onClick={handleClick}
      className={`theme-toggle ${className}`}
      aria-label={`Switch to ${mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light'} mode`}
      title={getLabel()}
    >
      <span className="theme-toggle-icon">{getIcon()}</span>
      <span className="theme-toggle-label">{getLabel()}</span>
    </button>
  );
}

/**
 * Component that automatically adjusts text color based on background
 */
interface AutoContrastTextProps {
  backgroundColor: string;
  children: React.ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

export function AutoContrastText({
  backgroundColor,
  children,
  className = '',
  as: Component = 'span'
}: AutoContrastTextProps) {
  const { getContrastText } = useTheme();
  const textColor = getContrastText(backgroundColor);

  return (
    <Component
      className={className}
      style={{ color: textColor }}
    >
      {children}
    </Component>
  );
}

/**
 * Hook to get theme-aware styles
 */
export function useThemedStyles() {
  const { actualMode } = useTheme();
  const theme = themeConfig[actualMode];

  return {
    applyTheme: (styles: React.CSSProperties): React.CSSProperties => {
      if (styles.backgroundColor && !styles.color) {
        styles.color = getContrastText(styles.backgroundColor as string);
      }
      return styles;
    },
    card: {
      backgroundColor: theme.background.elevated,
      color: theme.text.primary,
      border: `1px solid ${theme.border.default}`,
      borderRadius: themeConfig.borderRadius.lg,
      padding: themeConfig.spacing.lg,
      boxShadow: themeConfig.shadows.sm,
    } as React.CSSProperties,
    button: {
      primary: {
        backgroundColor: theme.components.button.primary,
        color: '#ffffff',
        border: 'none',
        borderRadius: themeConfig.borderRadius.md,
        padding: `${themeConfig.spacing.sm} ${themeConfig.spacing.md}`,
        cursor: 'pointer',
        transition: themeConfig.transitions.fast,
      } as React.CSSProperties,
      secondary: {
        backgroundColor: theme.components.button.secondary,
        color: theme.text.primary,
        border: `1px solid ${theme.border.default}`,
        borderRadius: themeConfig.borderRadius.md,
        padding: `${themeConfig.spacing.sm} ${themeConfig.spacing.md}`,
        cursor: 'pointer',
        transition: themeConfig.transitions.fast,
      } as React.CSSProperties,
    },
    input: {
      backgroundColor: theme.components.input.background,
      color: theme.components.input.text,
      border: `1px solid ${theme.components.input.border}`,
      borderRadius: themeConfig.borderRadius.md,
      padding: `${themeConfig.spacing.sm} ${themeConfig.spacing.md}`,
      fontSize: themeConfig.typography.fontSize.base,
      transition: themeConfig.transitions.fast,
    } as React.CSSProperties,
    table: {
      backgroundColor: theme.components.table.rowBg,
      color: theme.text.primary,
      borderColor: theme.components.table.border,
    } as React.CSSProperties,
  };
}

export default ThemeProvider;
